import type { UnconfirmedRow } from '#services/api/mia.ts';

import { getUnconfirmedRowRiskProbability } from '#services/api/patient-readiness.ts';

/**
 * The product name the risk-model modal uses (2026-07-29 comp). Scoped to that modal — other
 * surfaces are deliberately unbranded, and a broader adoption of the name is its own change.
 */
export const productName = 'Coordinate';

/**
 * The one term for the predicted risk, reached through {@link attendanceRiskCopy} rather than
 * imported bare. Kind-agnostic on purpose: wording never forks on the org's `riskKind`, so a
 * late-cancel practice can't be shown no-show wording. The kind still selects which model the modal
 * explains and which outcome its comparison line names; it just never reaches the words below.
 */
const riskNoun = 'attendance risk';

/**
 * The umbrella wording, composed from {@link riskNoun} and {@link productName} rather than spelled
 * out, so each has exactly one definition and the longer strings cannot drift from the short ones.
 */
export const attendanceRiskCopy = {
  /** Trails "…with the highest " in the briefing headline, which names the ranked slice. */
  briefingHeadlinePhrase: riskNoun,
  /**
   * The unconfirmed bucket card's metric label. Names what the card counts rather than the prediction
   * behind it: the number is every appointment still awaiting confirmation, whatever the model made
   * of it. Labels that named the risk kept misdescribing it — one forked on the org's risk kind, and
   * a later one said "highest", which belongs to the slice the briefing narrows to, not this total.
   */
  bucketMetricLabel: 'Unconfirmed Appointments',
  modalTitle: `How ${productName} predicts ${riskNoun}`,
  /** The noun in the threshold sentence ("…predicts their attendance risk is at or above…"). */
  riskNoun,
} as const;

/**
 * The share of the worklist the briefing surfaces for an org that has no threshold of its own.
 * Matches the "top ~10%" the per-org recommended threshold aims at, so an org's flagged count barely
 * moves on the day a real recommendation replaces this estimate.
 */
export const fallbackFlaggedShare = 0.09;

/**
 * The probability floor that would surface roughly `share` of `rows` — an estimate of the org's own
 * threshold, taken from the rows already on screen.
 *
 * Only for orgs the server reports a `fallback` threshold for. That fallback is a single global
 * constant, so it flags everything or nothing depending on where an org's distribution happens to
 * sit; a floor read off this org's own predictions at least surfaces its riskiest few. A `saved` or
 * `recommended` threshold is a real per-org number and must be used as given — deriving over the top
 * of `recommended` would permanently shadow the very value meant to supersede this.
 *
 * The honest limits, both inherited from being a relative share: the result makes no absolute risk
 * claim (the same patient may clear it one day and not the next, depending who else is on the list),
 * and for a short list the target rounds to one, so it returns that list's maximum and surfaces the
 * single riskiest appointment. Null when nothing is scored — there is no distribution to read.
 */
export function deriveFallbackThreshold(rows: UnconfirmedRow[], share: number): number | null {
  const descending = rows
    .map(getUnconfirmedRowRiskProbability)
    .filter((probability): probability is number => probability !== null)
    .sort((left, right) => right - left);

  if (descending.length === 0) {
    return null;
  }

  // The share is of every at-risk row, scored or not — an unscored row still occupies the day's
  // attention — but the floor can only ever be read off a scored one, hence the cap.
  const targetCount = Math.min(descending.length, Math.max(1, Math.round(share * rows.length)));

  return descending[targetCount - 1] ?? null;
}

/**
 * The rows the briefing flags: those the model scored at or above the org's threshold, ranked by
 * predicted probability.
 *
 * Unscored rows are excluded, not ranked last. A null probability is unknown risk, so flagging one
 * would present an appointment as high-risk on no evidence — which also lets the caller tell "we
 * couldn't rank these" apart from "nothing cleared the bar".
 *
 * There is deliberately no minimum. An absolute floor means an empty result is a real answer —
 * nothing at this practice is predicted risky enough to lead with today — and padding it with the
 * least-safe row available would flag an appointment the org's own threshold says is fine. (A
 * relative share needs a floor of one, because a small list rounds down to nothing for arithmetic
 * reasons rather than for any fact about the patients; {@link deriveFallbackThreshold} keeps that
 * floor for exactly that reason.)
 *
 * Rows tied at the threshold are all flagged, so a threshold derived to surface ~9% can surface more
 * when the boundary probability repeats. Equal predicted risk earning equal treatment beats cutting
 * the tie on arrival order to hit a target count.
 *
 * Ties keep the order they arrive in — the fetch boundary sorts equal risk by soonest appointment,
 * and the sort here is stable.
 */
export function selectFlaggedRows(rows: UnconfirmedRow[], threshold: number): UnconfirmedRow[] {
  return rows
    .flatMap((row) => {
      const probability = getUnconfirmedRowRiskProbability(row);

      return probability === null || probability < threshold ? [] : [{ row, probability }];
    })
    .sort((left, right) => right.probability - left.probability)
    .map((entry) => entry.row);
}
